import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

const BUBBLE_MAX = 288;

/**
 * An ⓘ that explains something: a tap opens it (so does hovering, where a mouse is used); a second
 * tap, a tap outside, Escape or scrolling closes it. It stands in for explanatory text under
 * headings and fields. Legal and consent wording, warnings and errors stay on the page instead.
 */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState<null | 'hover' | 'tap'>(null);
  const [at, setAt] = useState<{ left: number; top: number; width: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const id = useId();
  const show = useCallback((how: 'hover' | 'tap') => {
    const r = button.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(BUBBLE_MAX, window.innerWidth - 32);
    // Under the icon, kept 16 px inside the screen.
    const left = Math.min(Math.max(16, r.left + r.width / 2 - width / 2), window.innerWidth - 16 - width);
    setAt({ left, top: r.bottom + 6, width });
    setOpen(how);
  }, []);
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!button.current?.contains(t) && !bubble.current?.contains(t)) setOpen(null);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(null);
        button.current?.focus();
      }
    };
    const away = () => setOpen(null);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, [open]);
  const mouse = typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches;
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={`About ${label}`}
        aria-expanded={Boolean(open)}
        aria-controls={id}
        onClick={() => (open === 'tap' ? setOpen(null) : show('tap'))}
        onMouseEnter={mouse ? () => !open && show('hover') : undefined}
        onMouseLeave={mouse ? () => open === 'hover' && setOpen(null) : undefined}
        className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full align-middle text-muted normal-case tracking-normal before:absolute before:-inset-2.5 before:content-[''] hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5M12 8h.01" />
        </svg>
      </button>
      {/* A live region, so screen readers read the text when it opens. */}
      <span id={id} role="status">
        {open && at && (
          <span
            ref={bubble}
            className="fixed z-40 block rounded-xl border border-line bg-card p-3 text-left text-sm font-medium leading-snug text-ink2 normal-case tracking-normal shadow-xl"
            style={{ left: at.left, top: at.top, width: at.width }}
          >
            {children}
          </span>
        )}
      </span>
    </>
  );
}
